/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useContext, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useModal } from "../../../context/ModalContext";
import { ThemeContext } from "../../../context/ThemeContext";
import MainHeader from "../../../components/header/MainHeader";
import AddressSearchPreview from "../AddressSearchPreview";
import { formatCurrency } from "../../../utils/format";
import { useCurrencyFormat } from "./hooks/useCurrencyFormat";
import FloatingInput from "../../../components/input/FloatingInput";
import FloatingNumber from "../../../components/input/FloatingNumber";
import CustomSelect from "../../../components/customselect/CustomSelect";
import ResultsModal from "./components/ResultsModal";
import ManualSheetSection from "./components/ManualSheetSection";
import PropertyDetailsSection from "./components/PropertyDetailsSection";
import { getCityStateZipCode } from "./services/titleEscrowService";
import { getBuyerCalc } from "./services/buyerNetService";
import { CommonHeader } from "../../../components/ui/CommonHeader";
import Toast from "../../../components/common/Toast";

// Import custom hooks
import { useBuyerNetForm } from "./hooks/useBuyerNetForm";
import { useBuyerNetEngine } from "./hooks/useBuyerNetEngine";
import { useBuyerNetActions } from "./hooks/useBuyerNetActions";
import {
  usePrepaidEffects,
  defaultEst,
  parseCurrency,
  computeCommission,
} from "./utils/buyerNet.shared";
import { computeClosingCostTotals } from "./utils/calculations";

import "../../../calculater.css";
import "../../../responsiveness.css";

const BuyerNetSheet = () => {
  const theme = useContext(ThemeContext);

  const mainId = theme?.id || "";
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id;
  const postalCode = user?.postal_code;

  const { openModal, closeModal } = useModal();

  const [companyId, setCompanyId] = useState(user?.companyid || 0);
  const [main_company_name] = useState(theme?.main_company_name || "");

  const params = useParams();
  const calcId = params?.id || 0;
  const [searchParams] = useSearchParams();
  const isQuickMode = searchParams.get("mode") === "quick";

  const [ttbData, setTtbData] = useState({});
  const [salesPriceError] = useState("");
  const [mortgageAmountError] = useState("");
  const [isCorrectProperty, setIsCorrectProperty] = useState(true);
  const [showPreviewFromChild, setShowPreviewFromChild] = useState(false);
  const [useMortgageBalance, setUseMortgageBalance] = useState(true);
  const [useAnnualPropertyTax, setUseAnnualPropertyTax] = useState(true);
  const [useHomeWarranty, setUseHomeWarranty] = useState(false);
  const [showAddressView, setShowAddressView] = useState(false);
  const [showManualSheet, setShowManualSheet] = useState(false);
  const [quickModeZipReady, setQuickModeZipReady] = useState(false);
  const quickManualSheetOpenedRef = useRef(false);
  const [toast, setToast] = useState({
    message: "",
    visible: false,
    type: "success",
  });

  // Custom hooks
  const {
    formData,
    setFormData,
    templateformData,
    setTemplateformData,
    isEditLoading,
    setIsEditLoading,
    isInitialLoad,
    setIsInitialLoad,
    resetForm,
    resetData,
    setValue,
    getValues,
    control,
    watch,
    reset,
  } = useBuyerNetForm(mainId, userId, companyId, setCompanyId);

  // Save/Download/Print/Email all run from inside ResultsModal, whose
  // element (and every prop/closure baked into it, including
  // useBuyerNetActions' handlers) is cached by ModalContext and NOT
  // recreated on every re-render — `formData` read directly at that point
  // would still be whatever it was the moment the modal was first opened.
  // Reading `.current` inside a handler body at call time always gets the
  // live value regardless of which render's closure is actually invoked.
  const latestFormDataRef = useRef(formData);
  useEffect(() => {
    latestFormDataRef.current = formData;
  }, [formData]);

  const handleUpdateToaster = ({ message, visible, type = "success" }) => {
    setToast({ message, visible, type });
    setTimeout(
      () => setToast({ message: "", visible: false, type: "success" }),
      5000,
    );
  };

  const {
    getTitleEscrowData,
    handleLoanAmount,
    handleCalAmount,
    handleAddressEdited,
    handleAddressEnriched,
  } = useBuyerNetEngine(
    formData,
    setFormData,
    templateformData,
    setTemplateformData,
    isEditLoading,
    main_company_name,
    handleUpdateToaster,
    mainId,
    ttbData,
    useAnnualPropertyTax,
    getValues,
    setValue,
  );

  const { handleSaveBuyerCalc, handleDownloadPrintPDF, getEffectiveFormData } =
    useBuyerNetActions(
      openModal,
      closeModal,
      formData,
      setFormData,
      companyId,
      handleUpdateToaster,
      isQuickMode,
      mainId,
      latestFormDataRef,
      templateformData,
    );

  // Use prepaid effects
  usePrepaidEffects(formData, setFormData, isEditLoading, setValue);

  // Currency formatting hook
  const handleCurrencyBlur = useCurrencyFormat(formData, setFormData);

  const handleClosingCostUpdate = (camelKey, field, value) => {
    setFormData((prev) => {
      const updatedList = prev.closingCostsMeta.map((item) => {
        if (item.camelKey !== camelKey) return item;

        return {
          ...item,
          [field]: value,
          isManual: true,
        };
      });

      return computeClosingCostTotals({
        ...prev,
        closingCostsMeta: updatedList,
      });
    });
  };

  // Auto-calculate commission amounts on initial load
  useEffect(() => {
    if (isInitialLoad) return;

    const sp = parseCurrency(formData.salesPrice);
    if (!sp || sp < 50000) return;

    const comp = formData?.compensationMeta;
    if (!comp) return;

    const hasPercents =
      parseCurrency(comp.sellerBrokerCompensation) > 0 ||
      parseCurrency(comp.buyerBrokerCompensation) > 0;

    const noAmounts =
      parseCurrency(comp.sellerBrokerCompensationAmount) === 0 &&
      parseCurrency(comp.buyerBrokerCompensationAmount) === 0;

    if (hasPercents && noAmounts) {
      // Functional updater, not setFormData(computeCommission(formData, true))
      // — computeCommission shallow-spreads whatever formData it's given
      // ({...formData, compensationMeta, totalClosingMeta}), so calling it
      // with this effect's own closure and then replacing state directly
      // would silently discard any other update that committed between
      // this closure being captured and this effect actually running
      // (e.g. ManualSheetSection's Origination Fee % -> $ conversion).
      setFormData((prev) => computeCommission(prev, true));
    }
  }, [
    formData?.salesPrice,
    formData?.compensationMeta?.sellerBrokerCompensation,
    formData?.compensationMeta?.buyerBrokerCompensation,
    formData?.compensationMeta?.sellerBrokerCompensationAmount,
    formData?.compensationMeta?.buyerBrokerCompensationAmount,
    isInitialLoad,
    setFormData,
  ]);

  // Load existing calc (Edit Mode)
  useEffect(() => {
    if (calcId) {
      const loadExisting = async () => {
        try {
          setIsEditLoading(true);

          const resp = await getBuyerCalc(calcId);
          if (!resp) return;

          const existingData = resp || {};

          setShowManualSheet(true);

          setFormData(existingData);
          reset(existingData);

          setTimeout(() => {
            setIsEditLoading(false);
          }, 500);
        } catch (error) {
          console.error("Error loading calc:", error);
        }
      };

      loadExisting();
    } else {
      setIsEditLoading(false);
    }
  }, [calcId, setFormData, reset, setIsEditLoading]);

  // Quick mode ZIP verification
  useEffect(() => {
    if (!mainId) return;
    callQuickMode(mainId, isQuickMode, postalCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainId, isQuickMode, postalCode]);

  // Open manual sheet once quick-mode ZIP is set and template formData is ready
  useEffect(() => {
    if (!isQuickMode || !quickModeZipReady) return;
    if (!formData?.titleSettlementMeta) return;
    if (quickManualSheetOpenedRef.current) return;
    quickManualSheetOpenedRef.current = true;
    setShowManualSheet(true);
  }, [isQuickMode, quickModeZipReady, Boolean(formData?.titleSettlementMeta)]);

  const callQuickMode = (mainId, isQuickMode, postalCode) => {
    if (isQuickMode) {
      setIsInitialLoad(false);
      setShowAddressView(false);
      setIsEditLoading(false);
      setQuickModeZipReady(false);
      quickManualSheetOpenedRef.current = false;

      getCityStateZipCode({ zip: postalCode || "" })
        .then((res) => {
          if (res?.status === "fail") {
            handleUpdateToaster({
              message:
                "The address you entered is incorrect or the State/County is not supported.",
              visible: true,
              type: "error",
            });
            return;
          }

          const body = res?.data;
          if (body && res.status === "success") {
            try {
              if (body?.ttb) {
                setTtbData({
                  taxAmount: body.ttb.taxAmount || 0,
                  ...body.ttb,
                });
              }

              const prevAddr =
                (typeof getValues === "function"
                  ? getValues("addressMeta")
                  : null) || {};
              const prevLoan =
                (typeof getValues === "function"
                  ? getValues("loanInfoMeta")
                  : null) || {};

              setFormData((prev) => {
                const baseAddr = prev?.addressMeta || prevAddr || {};
                const baseLoan = prev?.loanInfoMeta || prevLoan || {};

                const addressMeta = {
                  ...baseAddr,
                  // Quick mode: use logged-in user's ZIP; do not set companyid from
                  // ZIP API (same as seller) — avoids template reload/overwrite.
                  companyid:
                    baseAddr?.companyid || user?.companyid || "",
                  city: body.city || "",
                  stateid: body.stateid,
                  state: body.state_code || "",
                  countyid: body.countyid,
                  county: body.county_name || "",
                  zip: postalCode || "",
                  unitNumber:
                    body.site_unit || baseAddr?.unitNumber || "",
                  addressIncludesUnit:
                    body.addressIncludesUnit ||
                    baseAddr?.addressIncludesUnit ||
                    "",
                  closing_state: body.closing_state || "",
                  closing_county: body.closing_county,
                  closing_city: body.closing_city,
                };

                const loanInfoMeta = {
                  ...baseLoan,
                  buyersLoanType: "Conventional",
                  estimatedClosingDate: defaultEst,
                  annualPropertyTax: body?.ttb?.taxAmount ?? 0.0,
                  annualPropertyTaxAmount: body?.ttb?.taxAmount ?? 0.0,
                  loanBalance: body?.ttb?.loanBalance ?? {},
                };

                const updated = {
                  ...prev,
                  addressVerified: true,
                  loanInfoMeta,
                  addressMeta,
                };

                // Sync to RHF so GeneralInformationSection shows city/state/zip/county
                setValue("addressMeta", addressMeta, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
                setValue("addressVerified", true, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
                setValue("loanInfoMeta", loanInfoMeta, {
                  shouldDirty: true,
                  shouldTouch: true,
                });

                setTimeout(() => {
                  getTitleEscrowData(
                    updated,
                    templateformData,
                    "Conventional",
                  );
                }, 300);

                return updated;
              });

              setQuickModeZipReady(true);
            } catch (err) {
              console.warn("verifyZip error:", err);
              handleUpdateToaster({
                message: "Unable to verify ZIP right now. Please try again.",
                visible: true,
                type: "error",
              });
            }
          }
        })
        .catch(() => {
          handleUpdateToaster({
            message: "Unable to verify ZIP right now. Please try again.",
            visible: true,
            type: "error",
          });
        });
    } else {
      setShowAddressView(true);
    }
  };

  const callZipOnChange = () => {
    if (isEditLoading) return;
    if (!formData?.addressMeta?.zip) return;
    if (!formData?.addressVerified) return;
    if (!formData?.loanInfoMeta?.buyersLoanType) return;
  
    getTitleEscrowData(
      formData,
      templateformData,
      formData?.loanInfoMeta?.buyersLoanType,
    );
  };

  const callOnBuyerDownPaymentChange = () => {
    if (isEditLoading) return;
    if (!formData?.loanInfoMeta?.buyerDownPayment) return;
    if (!formData?.addressVerified) return;
    if (!formData?.loanInfoMeta?.buyersLoanType) return;
    getTitleEscrowData(
      formData,
      templateformData,
      formData?.loanInfoMeta?.buyersLoanType,
    );
  };

  const callOnFirstLoanChange = (snapshot = null) => {
    if (isEditLoading) return;
    const dataToUse = snapshot || formData;
    if (!dataToUse.loanInfoMeta?.conventionalPercent) return;
    handleLoanAmount(dataToUse, "firstLoan");
  };

  const callOnSecondLoanChange = (snapshot = null) => {
    if (isEditLoading) return;
    const dataToUse = snapshot || formData;
    if (!dataToUse.loanInfoMeta?.conventionalPercent2) return;
    handleLoanAmount(dataToUse, "secondLoan");
  };

  const handleNewCalc = () => {
    setFormData({});
    resetForm();
    resetData(templateformData);
    setTtbData({});
    setIsCorrectProperty(false);
    setUseAnnualPropertyTax(useAnnualPropertyTax);
    setUseMortgageBalance(false);
    setUseHomeWarranty(false);
    setShowPreviewFromChild(false);
    setIsInitialLoad(false);
    setShowManualSheet(false);

    callQuickMode(mainId, isQuickMode, postalCode);
  };

  const checkValidSalesPrice = () => {
    const salesPrice = parseCurrency(formData.salesPrice);
    if (!salesPrice) {
      handleUpdateToaster({
        message: "Failure! Please enter a Sales Price",
        visible: true,
        type: "error",
      });
    }
    return !isNaN(salesPrice) && salesPrice !== 0;
  };

  const handleCancelModal = () => {
    closeModal();
    setTimeout(() => {
      openModal({
        title: "Buyer's Closing Costs Results",
        size: "halffull",
        content: renderModal(1),
      });
    }, 50);
  };

  const handleCancelResultModal = () => {
    closeModal();
    setTimeout(() => {
      openModal({
        title: "What would you like to do next?",
        size: "smallmodel",
        content: renderModal(2),
      });
    }, 50);
  };

  let finalCosts;
  const renderModal = (currentStep) => (
    <ResultsModal
      step={currentStep}
      setStep={(newStep) => {
        openModal({
          title: "What would you like to do next?",
          size: "smallmodel",
          content: renderModal(newStep),
        });
      }}
      closeModal={closeModal}
      onEditManual={() => {
        closeModal();
        setShowManualSheet(true);
      }}
      cancelModal={handleCancelModal}
      formData={finalCosts}
      result={finalCosts}
      formatCurrency={formatCurrency}
      handleSaveCalc={handleSaveBuyerCalc}
      handleDownloadPrintPDF={handleDownloadPrintPDF}
      getEffectiveFormData={getEffectiveFormData}
      cancelResultModal={handleCancelResultModal}
      showToaster={handleUpdateToaster}
      buyerformData={formData}
      templateformData={templateformData}
    />
  );

  const runCalculation = async () => {
    await getTitleEscrowData(
      formData,
      templateformData,
      formData?.loanInfoMeta?.buyersLoanType || "Conventional",
    );
    // Allow React to flush state updates before opening modal
    setTimeout(() => {
      openModal({
        title: "Buyer's Closing Costs Results",
        size: "halffull",
        content: renderModal(1),
      });
    }, 100);
  };

  const handleCalculateClick = async () => {
    if (!formData?.salesPrice || Number(formData.salesPrice) === 0) {
      openModal({
        title: "Warnings",
        size: "smallmodel",
        content: (
          <div className="modelspace application-fe">
            <p className="model-desc-details notice-icon">
              You haven&apos;t entered a Sales Price.
              <br />
              Do you want to continue without a Sales Price?
            </p>
            <div className="frm-btn popup-2-btn">
              <button
                className="submit-btn btn-bg inpt-btn btn-discard"
                onClick={() => closeModal()}
              >
                Cancel
              </button>
              <button
                className="submit-btn btn-bg inpt-btn"
                onClick={() => {
                  closeModal();
                  runCalculation();
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ),
      });

      return;
    }

    await runCalculation();
  };

  return (
    <div className="calculater-layout">
      <div className="calculator-header">
        <MainHeader />
      </div>

      <div>
        <div className="back-inner">
          {!showManualSheet && (
            <CommonHeader
              title={"Buyer's Closing Costs"}
              subTitle={`Enter the property address and sales price to get started.`}
            />
          )}
        </div>
      </div>

      <div
        className={`calc-container ${
          showManualSheet === true ? "calc-page" : " double-height"
        }`}
      >
        <div className="clc-mid-contain">
          {!showManualSheet && showAddressView && (
            <AddressSearchPreview
              mode="buyer"
              openModal={openModal}
              closeModal={closeModal}
              onEnriched={(data) =>
                handleAddressEnriched(data, {
                  setTtbData,
                  setCompanyId,
                  setShowPreviewFromChild,
                  setIsInitialLoad,
                  setIsCorrectProperty,
                })
              }
              onAddressEdited={(val) =>
                handleAddressEdited(val, {
                  setIsCorrectProperty,
                  setIsInitialLoad,
                  setUseAnnualPropertyTax,
                  setUseMortgageBalance,
                  setUseHomeWarranty,
                })
              }
              control={control}
              setValue={setValue}
              getValues={getValues}
            />
          )}

          {showPreviewFromChild && !showManualSheet && (
            <PropertyDetailsSection
              ttbData={ttbData}
              formatCurrency={formatCurrency}
              salesPriceError={salesPriceError}
              mortgageAmountError={mortgageAmountError}
              isCorrectProperty={isCorrectProperty}
              isInitialLoad={isInitialLoad}
              setIsCorrectProperty={setIsCorrectProperty}
              setIsInitialLoad={setIsInitialLoad}
              useAnnualPropertyTax={useAnnualPropertyTax}
              setUseAnnualPropertyTax={setUseAnnualPropertyTax}
              useMortgageBalance={useMortgageBalance}
              setUseMortgageBalance={setUseMortgageBalance}
              useHomeWarranty={useHomeWarranty}
              setUseHomeWarranty={setUseHomeWarranty}
              formData={formData}
              setFormData={setFormData}
              setValue={setValue}
              handleCurrencyBlur={handleCurrencyBlur}
              handleCalculate={handleCalculateClick}
              templateformData={templateformData}
            />
          )}
        </div>

        {showManualSheet && (
          <ManualSheetSection
            mainId={mainId}
            control={control}
            setValue={setValue}
            getValues={getValues}
            watch={watch}
            templateformData={templateformData}
            FloatingInput={FloatingInput}
            FloatingNumber={FloatingNumber}
            CustomSelect={CustomSelect}
            formatCurrency={formatCurrency}
            salesPriceError={salesPriceError}
            mortgageAmountError={mortgageAmountError}
            isCorrectProperty={isCorrectProperty}
            setIsCorrectProperty={setIsCorrectProperty}
            useAnnualPropertyTax={useAnnualPropertyTax}
            setUseAnnualPropertyTax={setUseAnnualPropertyTax}
            ttbData={ttbData}
            onAddressEnriched={(data) =>
              handleAddressEnriched(data, {
                setTtbData,
                setCompanyId,
                setShowPreviewFromChild,
                setIsInitialLoad,
                setIsCorrectProperty,
              })
            }
            useMortgageBalance={useMortgageBalance}
            setUseMortgageBalance={setUseMortgageBalance}
            handleCurrencyBlur={handleCurrencyBlur}
            handleCalculate={handleCalculateClick}
            computeCommission={computeCommission}
            checkValidSalesPrice={checkValidSalesPrice}
            formData={formData}
            setFormData={setFormData}
            handleCalAmount={handleCalAmount}
            onCloseManual={() => {
              handleNewCalc();
            }}
            callZipOnChange={callZipOnChange}
            callOnFirstLoanChange={callOnFirstLoanChange}
            callOnBuyerDownPaymentChange={callOnBuyerDownPaymentChange}
            callOnSecondLoanChange={callOnSecondLoanChange}
            handleSaveCalc={handleSaveBuyerCalc}
            handleDownloadPrintPDF={handleDownloadPrintPDF}
            showToaster={handleUpdateToaster}
            setCompanyId={setCompanyId}
            handleClosingCostUpdate={handleClosingCostUpdate}
            isQuickMode={isQuickMode}
          />
        )}

        <Toast
          message={toast.message}
          type={toast.type}
          visible={toast.visible}
        />
      </div>
    </div>
  );
};

export default BuyerNetSheet;
